import React, { Fragment, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import style from './map.css';
import { getWorldColor, formatSumData, classNames } from '../../utils/utils';
import worldNamesMap from '../../../data/worldMap/worldMap.json';

let initZoom = 1; // 地图缩放

/* 世界地图 */
const Map = function Map(props) {
    const { area } = props;
    const mapRootRef = useRef(null); // echarts div
    const echartsRef = useRef(null); // echarts

    // 渲染地图
    const mapRender = function mapRender() {
        const areaData = [...area];
        const names = areaData.map(item => item.country); // 国家名称

        Object.values(worldNamesMap).forEach(item => {
            if (names.indexOf(item) < 0) {
                areaData.push({
                    country: item,
                    date: '',
                    vaccinebrands: '',
                    total: 0,
                    per_hundred: 0,
                });
            }
        });

        const options = {
            tooltip: {
                trigger: 'item',
                showDelay: 0,
                enterable: true,
                transitionDuration: 0.2,
                padding: 8,
                borderWidth: 1,
                extraCssText:
                    'z-index: 2; border-radius: 8px; background: rgba(0, 0, 0, .6); box-shadow: 0 0 4 0 rgba(219, 219, 219, .5)',
                textStyle: {
                    color: '#ffffff',
                    fontSize: 12,
                    padding: 5,
                },
                formatter({ data }) {
                    if (!data) {
                        return;
                    }

                    const v0 = formatSumData(data.num[0]);
                    const v1 = formatSumData(data.num[1]);

                    return `<div style="padding: 10px 12px 14px 12px; font-size: 24px; line-height: 32px; color: #fff;">
    <div style="margin-bottom: 16px;">${data.name}</div>
    <div style="margin-bottom: 16px;">累计接种：${v0.num}${v0.unit}剂</div>
    <div>每百人接种：${v1.num}${v1.unit}剂</div>
</div>`;
                },
            },
            series: [
                {
                    name: '世界',
                    type: 'map',
                    mapType: 'world',
                    left: 0,
                    right: 0,
                    top: 20,
                    zoom: initZoom, // 地图缩放
                    roam: initZoom > 1,
                    scaleLimit: {
                        max: 5,
                        min: 1,
                    },
                    itemStyle: {
                        normal: {
                            borderColor: '#ffffff', // 边框颜色
                            borderWidth: 0.5, // 边框线宽
                        },
                        emphasis: {
                            areaColor: '#8ef2f2',
                            label: {
                                show: false,
                            },
                        },
                    },
                    label: {
                        show: false,
                    },
                    data: areaData.map(item => {
                        let borderColor = '';

                        if (item.country === '中国') {
                            borderColor = '#ffffff';
                        } else {
                            borderColor = item.total < 3000 ? '#b9b9b9' : '#ffffff';
                        }

                        return {
                            name: item.country,
                            // selected: item.country,
                            itemStyle: {
                                normal: {
                                    areaColor: getWorldColor(item.total),
                                    borderColor, // 边框颜色
                                    borderWidth: item.country === '中国' ? 1.5 : 0.5, // 边框线宽
                                },
                            },
                            num: [item.total, item.per_hundred],
                        };
                    }),
                    nameMap: worldNamesMap,
                },
            ],
        };

        echartsRef.current.setOption(options);
    };

    // 初始化地图
    const mapsInit = async function mapsInit() {
        const echarts = await import('echarts/lib/echarts');

        await Promise.all([
            import('echarts/map/js/world'),
            import('echarts/lib/component/tooltip'), // 引入提示框和标题组件
            import('echarts/lib/component/legend'),
            import('echarts/lib/component/legendScroll'),
        ]);

        echartsRef.current = echarts.init(mapRootRef.current);

        const worldMap = await import('../../../data/worldall/layout/map/world');

        echarts.registerMap('world', worldMap);
        mapRender();
    };

    // 放大地图
    const handleZoomInClick = function handleZoomInClick(event) {
        if (initZoom === 5) {
            return;
        }

        initZoom++;
        mapRender();
    };

    // 缩小地图
    const handleZoomOutClick = function handleZoomOutClick(event) {
        if (initZoom === 1) {
            return;
        }

        initZoom--;

        if (initZoom === 1) {
            echartsRef.current.dispose();
            mapsInit();
        } else {
            mapRender();
        }
    };

    /**
     * 重置地图
     * TODO: 拖拽完重置后无法还原到基本的位置，所以需要销毁后重新渲染
     */
    const handleResetClick = function handleResetClick(event) {
        initZoom = 1;
        echartsRef.current.dispose();
        mapsInit();
    };

    useEffect(() => {
        mapsInit();
    }, []);

    return (
        <Fragment>
            <div ref={mapRootRef} className={style.worldMap} />
            <div className={style.zoomBox}>
                <a
                    className={classNames(style.zoomBtn, style.zoomIconBtn, style.addIcon)}
                    role="button"
                    aria-label="放大"
                    onClick={handleZoomInClick}
                />
                <a
                    className={classNames(style.zoomBtn, style.zoomIconBtn, style.lessIcon)}
                    role="button"
                    aria-label="缩小"
                    onClick={handleZoomOutClick}
                />
                <a
                    className={classNames(style.zoomBtn, style.zoomIconText)}
                    role="button"
                    aria-label="重置"
                    onClick={handleResetClick}>
                    重置
                </a>
            </div>
        </Fragment>
    );
};

Map.propTypes = {
    area: PropTypes.array,
};

export default Map;
